import type { AIModel, SilkAIParameterField } from '@/api/types'
import type { AudioProductTab } from './types'
import type { ModelParamField } from './model-params'
import { inferAudioProductGroup } from './audio-tabs'
import {
  elevenLabsDefaultVoiceId,
  inferAudioFamily,
  isElevenLabsMusicModel,
  isElevenLabsTTSModel,
  isMurekaMusicModel,
  isSeedAudioModel,
} from './audio-compat'

const QWEN_INSTRUCT_TTS_VOICE_OPTIONS = [
  'Cherry',
  'Serena',
  'Ethan',
  'Chelsie',
  'Momo',
  'Vivian',
  'Moon',
  'Maia',
  'Kai',
  'Nofish',
  'Bella',
  'Eldric Sage',
  'Mia',
  'Mochi',
  'Bellona',
  'Vincent',
  'Bunny',
  'Neil',
  'Elias',
  'Arthur',
  'Nini',
  'Seren',
  'Pip',
  'Stella',
].map((v) => ({ value: v, labelKey: v }))

const QWEN_FLASH_TTS_VOICE_OPTIONS = [
  'Cherry',
  'Ethan',
  'Serena',
  'Chelsie',
  'Dylan',
  'Jada',
  'Sunny',
  'Nofish',
  'Jennifer',
  'Li',
  'Ryan',
  'Katerina',
  'Aiden',
].map((v) => ({ value: v, labelKey: v }))

/** Instruct 模型不支持 Jennifer/Jada/Dylan 等 Flash 专属音色 */
export function qwenVoiceOptions(modelId?: string) {
  return isQwenInstructModel(modelId) ? QWEN_INSTRUCT_TTS_VOICE_OPTIONS : QWEN_FLASH_TTS_VOICE_OPTIONS
}

const QWEN_INSTRUCT_VOICE_SET = new Set(QWEN_INSTRUCT_TTS_VOICE_OPTIONS.map((o) => o.value))
const QWEN_FLASH_VOICE_SET = new Set(QWEN_FLASH_TTS_VOICE_OPTIONS.map((o) => o.value))
const QWEN_SYSTEM_VOICE_SET = new Set([...QWEN_INSTRUCT_VOICE_SET, ...QWEN_FLASH_VOICE_SET])

export function isQwenSystemVoice(voiceId?: string): boolean {
  return QWEN_SYSTEM_VOICE_SET.has((voiceId ?? '').trim())
}

/** 百炼复刻/设计音色 ID（非 Cherry 等系统预设）。 */
export function isDashScopeDesignedVoiceId(voiceId?: string): boolean {
  const v = (voiceId ?? '').toLowerCase()
  return v.includes('qwen-tts-vd-') || v.includes('tts-vd-bailian')
}

export function isDashScopeClonedVoiceId(voiceId?: string): boolean {
  const v = (voiceId ?? '').trim()
  if (!v || isQwenSystemVoice(v) || isDashScopeDesignedVoiceId(v)) return false
  return isDashScopeCustomVoiceId(v)
}

export function isDashScopeVoiceIdCompatibleWithModel(modelId: string, voiceId?: string): boolean {
  const v = (voiceId ?? '').trim()
  if (!v) return false
  if (isQwenVCRealtimeModel(modelId)) return isDashScopeClonedVoiceId(v)
  if (isQwenVDRealtimeModel(modelId)) return isDashScopeDesignedVoiceId(v)
  return true
}

/** 百炼复刻/设计音色 ID（非 Cherry 等系统预设）。 */
export function isDashScopeCustomVoiceId(voiceId?: string): boolean {
  const v = (voiceId ?? '').trim()
  if (!v || isQwenSystemVoice(v)) return false
  const lower = v.toLowerCase()
  return (
    lower.includes('bailian-voice') ||
    lower.includes('qwen-tts-v') ||
    lower.startsWith('cosyvoice-') ||
    lower.startsWith('vc-') ||
    v.length > 24
  )
}

function resolveDashScopeCloneOrDesignVoiceId(payload: Record<string, unknown>): string {
  let vid = typeof payload.voice_id === 'string' ? payload.voice_id.trim() : ''
  const vp = typeof payload.voice_prompt === 'string' ? payload.voice_prompt.trim() : ''
  if (isDashScopeCustomVoiceId(vp) && (!isDashScopeCustomVoiceId(vid) || isQwenSystemVoice(vid))) {
    vid = vp
  }
  return vid
}

export function coerceQwenVoiceForModel(modelId: string, voiceId?: string): string {
  const v = (voiceId ?? '').trim()
  const allowed = isQwenInstructModel(modelId) ? QWEN_INSTRUCT_VOICE_SET : QWEN_FLASH_VOICE_SET
  if (v && allowed.has(v)) return v
  return 'Cherry'
}

const QWEN_LANGUAGE_OPTIONS = [
  { value: 'Auto', labelKey: 'Auto' },
  { value: 'Chinese', labelKey: '中文' },
  { value: 'English', labelKey: 'English' },
  { value: 'Japanese', labelKey: '日本語' },
  { value: 'Korean', labelKey: '한국어' },
]

const MINIMAX_VOICE_OPTIONS = [
  { value: 'male-qn-qingse', labelKey: 'canvas.params.mmVoiceQingse' },
  { value: 'male-qn-jingying', labelKey: 'canvas.params.mmVoiceJingying' },
  { value: 'male-qn-badao', labelKey: 'canvas.params.mmVoiceBadao' },
  { value: 'male-qn-daxuesheng', labelKey: 'canvas.params.mmVoiceDaxuesheng' },
  { value: 'female-shaonv', labelKey: 'canvas.params.mmVoiceShaonv' },
  { value: 'female-yujie', labelKey: 'canvas.params.mmVoiceYujie' },
  { value: 'female-chengshu', labelKey: 'canvas.params.mmVoiceChengshu' },
  { value: 'female-tianmei', labelKey: 'canvas.params.mmVoiceTianmei' },
  { value: 'presenter_male', labelKey: 'canvas.params.mmVoicePresenterMale' },
  { value: 'presenter_female', labelKey: 'canvas.params.mmVoicePresenterFemale' },
  { value: 'audiobook_male_1', labelKey: 'canvas.params.mmVoiceAudiobookMale' },
  { value: 'audiobook_female_1', labelKey: 'canvas.params.mmVoiceAudiobookFemale' },
  { value: 'clever_boy', labelKey: 'canvas.params.mmVoiceCleverBoy' },
  { value: 'lovely_girl', labelKey: 'canvas.params.mmVoiceLovelyGirl' },
]

const MINIMAX_SFX_OPTIONS = [
  { value: 'none', labelKey: 'canvas.params.sfxNone' },
  { value: 'spacious_echo', labelKey: 'canvas.params.sfxSpaciousEcho' },
  { value: 'auditorium_echo', labelKey: 'canvas.params.sfxAuditorium' },
  { value: 'lofi_telephone', labelKey: 'canvas.params.sfxTelephone' },
  { value: 'robotic', labelKey: 'canvas.params.sfxRobotic' },
]

export const MINIMAX_PROMPT_MAX = 50_000

export const MUREKA_PROMPT_MAX = 1024

export type MurekaResolvedParams = {
  music_mode: 'describe' | 'lyrics'
  instrumental: boolean
}

export function resolveMurekaParams(params?: Record<string, string | number>): MurekaResolvedParams {
  const next = normalizeAudioSubmitPayload('mureka-v8', { ...(params ?? {}) })
  return {
    music_mode: String(next.music_mode ?? 'describe') === 'lyrics' ? 'lyrics' : 'describe',
    instrumental: String(next.instrumental ?? 'false') === 'true',
  }
}

export function defaultMurekaParams(): MurekaResolvedParams {
  return resolveMurekaParams({})
}

export type MiniMaxResolvedParams = {
  voice_id: string
  speed: number
  pitch: number
  volume: number
  modify_pitch: number
  intensity: number
  timbre: number
  sound_effects: string
}

export function resolveMiniMaxParams(params?: Record<string, string | number>): MiniMaxResolvedParams {
  const next = normalizeAudioSubmitPayload('speech-2.8-hd', { ...(params ?? {}) })
  return {
    voice_id: String(next.voice_id ?? 'male-qn-qingse'),
    speed: Number(next.speed ?? 1),
    pitch: Number(next.pitch ?? 0),
    volume: Number(next.volume ?? 1),
    modify_pitch: Number(next.modify_pitch ?? 0),
    intensity: Number(next.intensity ?? 0),
    timbre: Number(next.timbre ?? 0),
    sound_effects: String(next.sound_effects ?? 'none'),
  }
}

export function defaultMiniMaxParams(): MiniMaxResolvedParams {
  return resolveMiniMaxParams({})
}

export { MINIMAX_SFX_OPTIONS }

export function isQwenDashScopeTTSModel(modelId?: string): boolean {
  const id = (modelId ?? '').toLowerCase()
  return id.includes('qwen3-tts') || id.includes('qwen-tts') || id.startsWith('cosyvoice-')
}

export function isQwenVCRealtimeModel(modelId?: string): boolean {
  const id = (modelId ?? '').toLowerCase()
  return id.includes('qwen3-tts-vc-realtime') || (id.includes('qwen3-tts-vc') && !id.includes('vd'))
}

export function isQwenVDRealtimeModel(modelId?: string): boolean {
  const id = (modelId ?? '').toLowerCase()
  return id.includes('qwen3-tts-vd-realtime') || (id.includes('qwen3-tts-vd') && !id.includes('vc'))
}

export function isQwenInstructModel(modelId?: string): boolean {
  return (modelId ?? '').toLowerCase().includes('qwen3-tts-instruct')
}

export function isMiniMaxSpeechModel(modelId?: string): boolean {
  return inferAudioFamily(modelId ?? '') === 'minimax_t2a'
}

/** 使用模型专属面板（非通用参数区）。 */
export function usesDedicatedAudioPanel(modelId?: string): boolean {
  return (
    isSeedAudioModel(modelId) ||
    isMiniMaxSpeechModel(modelId) ||
    isMurekaMusicModel(modelId)
  )
}

export function isIndexTTSModel(modelId?: string): boolean {
  return (modelId ?? '').toLowerCase().includes('indextts')
}

export function isSunoMusicModel(modelId?: string): boolean {
  const id = (modelId ?? '').toLowerCase().trim()
  if (!id || isElevenLabsMusicModel(id) || isMurekaMusicModel(id)) return false
  return id.includes('suno') || id.includes('music-v1') || id.startsWith('music-')
}

export { isElevenLabsMusicModel, isElevenLabsTTSModel, isMurekaMusicModel, isSeedAudioModel }

const CANVAS_VOICE_TO_QWEN: Record<string, string> = {
  male: 'Ethan',
  female: 'Cherry',
  narrator: 'Serena',
  child: 'Chelsie',
}

export function normalizeAudioSubmitPayload(
  modelId: string,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...payload }
  if (isMurekaMusicModel(modelId)) {
    delete out.voice
    delete out.voice_id
    delete out.duration
    delete out.mode
    const musicMode = String(out.music_mode ?? 'describe').trim() || 'describe'
    out.music_mode = musicMode === 'lyrics' ? 'lyrics' : 'describe'
    const inst = String(out.instrumental ?? 'false').toLowerCase()
    out.instrumental = inst === 'true' || inst === '1' || inst === 'on' ? 'true' : 'false'
    if (out.music_mode === 'lyrics') {
      if (!String(out.lyrics ?? '').trim() && typeof out.prompt === 'string') {
        out.lyrics = out.prompt
      }
    } else {
      delete out.lyrics
    }
    return out
  }
  if (isElevenLabsMusicModel(modelId)) {
    delete out.voice
    delete out.voice_id
    delete out.music_mode
    delete out.lyrics
    delete out.style
    delete out.mode
    const sec = Number(out.duration ?? 30)
    out.duration = [30, 60, 120, 180, 240].includes(sec) ? sec : 30
    return out
  }
  if (isElevenLabsTTSModel(modelId)) {
    delete out.voice
    delete out.duration
    delete out.language_type
    delete out.mode
    if (!String(out.voice_id ?? '').trim()) out.voice_id = elevenLabsDefaultVoiceId()
    const stab = String(out.stability ?? 'natural').trim().toLowerCase()
    out.stability = ['creative', 'natural', 'robust'].includes(stab) ? stab : 'natural'
    return out
  }
  if (isSeedAudioModel(modelId)) {
    delete out.voice
    delete out.voice_id
    delete out.duration
    delete out.language_type
    delete out.mode
    const lang = String(out.language ?? 'zh').trim() || 'zh'
    out.language = lang === 'en' || lang === 'English' || lang === '英文' ? 'en' : 'zh'
    const rate = Number(out.sample_rate ?? 24000)
    out.sample_rate = Number.isFinite(rate) ? rate : 24000
    const fmt = String(out.format ?? 'wav').trim().toLowerCase()
    out.format = ['wav', 'mp3', 'pcm', 'ogg_opus'].includes(fmt) ? fmt : 'wav'
    if (out.speed === undefined || out.speed === '') out.speed = 1
    if (out.pitch === undefined || out.pitch === '') out.pitch = 0
    if (out.volume === undefined || out.volume === '') out.volume = 1
    return out
  }
  if (isSunoMusicModel(modelId)) {
    out.mode = 'music'
    const musicMode = String(out.music_mode ?? 'describe').trim() || 'describe'
    delete out.music_mode
    if (musicMode === 'lyrics') {
      // 歌词生曲：保留 lyrics；style 作可选风格 tags
      if (typeof out.lyrics === 'string') out.lyrics = out.lyrics.trim()
      if (typeof out.style === 'string') out.style = out.style.trim()
    } else {
      // 描述生曲：用 prompt + style，不传歌词避免走歌词模式
      delete out.lyrics
      if (typeof out.style === 'string') out.style = out.style.trim()
    }
    return out
  }
  if (isMiniMaxSpeechModel(modelId)) {
    delete out.voice
    delete out.duration
    delete out.language_type
    delete out.mode
    if (!String(out.voice_id ?? '').trim()) out.voice_id = 'male-qn-qingse'
    if (out.speed === undefined || out.speed === '') out.speed = 1
    if (out.pitch === undefined || out.pitch === '') out.pitch = 0
    if (out.volume === undefined || out.volume === '') out.volume = 1
    if (out.modify_pitch === undefined || out.modify_pitch === '') out.modify_pitch = 0
    if (out.intensity === undefined || out.intensity === '') out.intensity = 0
    if (out.timbre === undefined || out.timbre === '') out.timbre = 0
    const sfx = String(out.sound_effects ?? 'none').trim()
    out.sound_effects = sfx && sfx !== 'none' ? sfx : 'none'
    return out
  }
  if (!isQwenDashScopeTTSModel(modelId)) {
    if (typeof out.voice === 'string' && !out.voice_id) {
      out.voice_id = out.voice
    }
    return out
  }
  if (isQwenVCRealtimeModel(modelId) || isQwenVDRealtimeModel(modelId)) {
    const vid = resolveDashScopeCloneOrDesignVoiceId(out)
    delete out.voice
    delete out.mode
    delete out.duration
    delete out.pitch
    delete out.speed
    delete out.voice_prompt
    if (vid && isDashScopeVoiceIdCompatibleWithModel(modelId, vid)) {
      out.voice_id = vid
    } else {
      delete out.voice_id
    }
    return out
  }
  if (typeof out.voice_id === 'string' && out.voice_id.trim()) {
    out.voice_id = coerceQwenVoiceForModel(modelId, out.voice_id)
    delete out.voice_prompt
    return out
  }
  const voice = typeof out.voice === 'string' ? out.voice.trim() : ''
  if (voice) {
    const mapped = CANVAS_VOICE_TO_QWEN[voice.toLowerCase()]
    if (mapped && !isQwenVCRealtimeModel(modelId) && !isQwenVDRealtimeModel(modelId)) {
      out.voice_id = mapped
    } else if (!mapped) {
      out.voice_id = voice
    }
  }
  delete out.voice_prompt
  return out
}

function schemaFieldToParam(f: SilkAIParameterField): ModelParamField | null {
  const key = f.key
  if (!key || key === 'prompt') return null
  const labelKey =
    key === 'voice_id'
      ? 'canvas.params.voice'
      : key === 'voice_prompt'
        ? 'canvas.params.voicePrompt'
        : key === 'instructions'
          ? 'canvas.params.instructions'
          : key === 'lyrics'
            ? 'canvas.params.lyrics'
            : key === 'style'
              ? 'canvas.params.musicStyle'
              : key === 'language_type'
                ? 'canvas.params.language'
                : `canvas.params.${key}`
  if (f.type === 'select' && f.options?.length) {
    return {
      key,
      labelKey,
      type: 'select',
      options: f.options.map((v) => ({ value: v, labelKey: v })),
      default: String(f.default ?? f.options[0] ?? ''),
    }
  }
  if (f.type === 'number') {
    return {
      key,
      labelKey,
      type: 'number',
      min: typeof f.min === 'number' ? f.min : undefined,
      max: typeof f.max === 'number' ? f.max : undefined,
      default: typeof f.default === 'number' ? f.default : Number(f.default ?? 0),
    }
  }
  if (f.type === 'string' || !f.type) {
    return {
      key,
      labelKey,
      type: 'text',
      default: String(f.default ?? ''),
    }
  }
  return null
}

function fieldsFromSchema(model?: AIModel, onlyKeys?: string[]): ModelParamField[] {
  const fields = model?.parameter_schema?.fields ?? []
  const out: ModelParamField[] = []
  for (const f of fields) {
    if (onlyKeys && !onlyKeys.includes(f.key)) continue
    const mapped = schemaFieldToParam(f)
    if (mapped) out.push(mapped)
  }
  return out
}

function qwenVoiceField(modelId?: string, model?: AIModel): ModelParamField {
  const schemaFields = fieldsFromSchema(model)
  const voiceFromSchema = schemaFields.find((f) => f.key === 'voice_id')
  const allowed = new Set(qwenVoiceOptions(modelId).map((o) => o.value))
  if (voiceFromSchema?.type === 'select' && voiceFromSchema.options?.length) {
    const options = voiceFromSchema.options.filter((o) => allowed.has(o.value))
    return {
      ...voiceFromSchema,
      options: options.length ? options : qwenVoiceOptions(modelId),
      default: coerceQwenVoiceForModel(modelId ?? '', String(voiceFromSchema.default ?? 'Cherry')),
    }
  }
  return {
    key: 'voice_id',
    labelKey: 'canvas.params.voice',
    type: 'select',
    options: qwenVoiceOptions(modelId),
    default: 'Cherry',
  }
}

function qwenNarrationFields(modelId?: string, model?: AIModel): ModelParamField[] {
  const schemaFields = fieldsFromSchema(model)
  const voiceField = qwenVoiceField(modelId, model)
  const langField: ModelParamField = {
    key: 'language_type',
    labelKey: 'canvas.params.language',
    type: 'select',
    options: QWEN_LANGUAGE_OPTIONS,
    default: 'Auto',
  }
  const fields: ModelParamField[] = [voiceField, langField]
  if (isQwenInstructModel(modelId)) {
    const ins = schemaFields.find((f) => f.key === 'instructions')
    fields.push(
      ins ?? {
        key: 'instructions',
        labelKey: 'canvas.params.instructions',
        type: 'text',
        default: '',
      },
    )
  }
  fields.push(
    { key: 'speed', labelKey: 'canvas.params.speed', type: 'number', min: 0.5, max: 2, default: 1 },
    { key: 'pitch', labelKey: 'canvas.params.pitch', type: 'number', min: -12, max: 12, default: 0 },
  )
  return fields
}

export const SEED_AUDIO_PROMPT_MAX = 2000

export const SEED_AUDIO_LANGUAGES = [
  { value: 'zh', labelKey: 'canvas.params.langZh' },
  { value: 'en', labelKey: 'canvas.params.langEn' },
] as const

export const SEED_AUDIO_SAMPLE_RATES = [
  { value: 8000, label: '8k' },
  { value: 16000, label: '16k' },
  { value: 24000, label: '24k' },
  { value: 48000, label: '48k' },
] as const

export const SEED_AUDIO_FORMATS = ['wav', 'mp3', 'pcm', 'ogg_opus'] as const

export type SeedAudioParams = {
  language: 'zh' | 'en'
  sample_rate: number
  format: (typeof SEED_AUDIO_FORMATS)[number]
  speed: number
  pitch: number
  volume: number
}

export function resolveSeedAudioParams(params?: Record<string, string | number>): SeedAudioParams {
  const langRaw = String(params?.language ?? 'zh').trim()
  const language: 'zh' | 'en' =
    langRaw === 'en' || langRaw === 'English' || langRaw === '英文' ? 'en' : 'zh'
  const rate = Number(params?.sample_rate ?? 24000)
  const sample_rate = SEED_AUDIO_SAMPLE_RATES.some((r) => r.value === rate) ? rate : 24000
  const fmt = String(params?.format ?? 'wav').trim().toLowerCase()
  const format = (SEED_AUDIO_FORMATS as readonly string[]).includes(fmt)
    ? (fmt as SeedAudioParams['format'])
    : 'wav'
  const speed = clampSeedNum(params?.speed, 1, 0.5, 2)
  const pitch = clampSeedNum(params?.pitch, 0, -12, 12)
  const volume = clampSeedNum(params?.volume, 1, 0.5, 2)
  return { language, sample_rate, format, speed, pitch, volume }
}

function clampSeedNum(raw: string | number | undefined, fallback: number, min: number, max: number): number {
  const n = Number(raw)
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

function seedAudioFields(): ModelParamField[] {
  return [
    {
      key: 'language',
      labelKey: 'canvas.params.language',
      type: 'select',
      options: [
        { value: 'zh', labelKey: 'canvas.params.langZh' },
        { value: 'en', labelKey: 'canvas.params.langEn' },
      ],
      default: 'zh',
    },
    {
      key: 'sample_rate',
      labelKey: 'canvas.params.sampleRate',
      type: 'select',
      options: [
        { value: '8000', labelKey: '8k' },
        { value: '16000', labelKey: '16k' },
        { value: '24000', labelKey: '24k' },
        { value: '48000', labelKey: '48k' },
      ],
      default: '24000',
    },
    {
      key: 'format',
      labelKey: 'canvas.params.outputFormat',
      type: 'select',
      options: [
        { value: 'wav', labelKey: 'wav' },
        { value: 'mp3', labelKey: 'mp3' },
        { value: 'pcm', labelKey: 'pcm' },
        { value: 'ogg_opus', labelKey: 'ogg_opus' },
      ],
      default: 'wav',
    },
    {
      key: 'speed',
      labelKey: 'canvas.params.speed',
      type: 'number',
      min: 0.5,
      max: 2,
      step: 0.01,
      default: 1,
    },
    {
      key: 'pitch',
      labelKey: 'canvas.params.pitchTone',
      type: 'number',
      min: -12,
      max: 12,
      step: 1,
      default: 0,
    },
    {
      key: 'volume',
      labelKey: 'canvas.params.volume',
      type: 'number',
      min: 0.5,
      max: 2,
      step: 0.1,
      default: 1,
    },
  ]
}

function minimaxFields(_model?: AIModel): ModelParamField[] {
  return [
    {
      key: 'voice_id',
      labelKey: 'canvas.params.voice',
      type: 'select',
      options: MINIMAX_VOICE_OPTIONS,
      default: 'male-qn-qingse',
    },
    { key: 'speed', labelKey: 'canvas.params.speed', type: 'number', min: 0.5, max: 2, step: 0.01, default: 1 },
    { key: 'pitch', labelKey: 'canvas.params.pitchTone', type: 'number', min: -12, max: 12, default: 0 },
    { key: 'volume', labelKey: 'canvas.params.volume', type: 'number', min: 0.1, max: 10, step: 0.1, default: 1 },
    { key: 'modify_pitch', labelKey: 'canvas.params.modifyPitch', type: 'number', min: -100, max: 100, default: 0 },
    { key: 'intensity', labelKey: 'canvas.params.modifyIntensity', type: 'number', min: -100, max: 100, default: 0 },
    { key: 'timbre', labelKey: 'canvas.params.modifyTimbre', type: 'number', min: -100, max: 100, default: 0 },
    {
      key: 'sound_effects',
      labelKey: 'canvas.params.soundEffects',
      type: 'select',
      options: MINIMAX_SFX_OPTIONS,
      default: 'none',
    },
  ]
}

const ELEVEN_VOICE_OPTIONS = [
  { value: 'kAIqZ7fZv234ClKXwzDx', labelKey: 'canvas.params.elVoiceSusan' },
  { value: '21m00Tcm4TlvDq8ikWAM', labelKey: 'canvas.params.elVoiceRachel' },
  { value: 'EXAVITQu4vr4xnSDxMaL', labelKey: 'canvas.params.elVoiceSarah' },
  { value: 'JBFqnCBsd6RMkjVDRZzb', labelKey: 'canvas.params.elVoiceGeorge' },
  { value: 'pFZP5JQG7iQjIQuC4Bku', labelKey: 'canvas.params.elVoiceLily' },
  { value: 'IKne3meq5aSn9XLyUdCD', labelKey: 'canvas.params.elVoiceCharlie' },
  { value: 'nPczCjzI2devNBz1zQrb', labelKey: 'canvas.params.elVoiceBrian' },
  { value: 'Xb7hH8MSUJpSbSDYk0k2', labelKey: 'canvas.params.elVoiceAlice' },
]

function elevenTTSFields(): ModelParamField[] {
  return [
    {
      key: 'voice_id',
      labelKey: 'canvas.params.voice',
      type: 'select',
      options: ELEVEN_VOICE_OPTIONS,
      default: elevenLabsDefaultVoiceId(),
    },
    {
      key: 'stability',
      labelKey: 'canvas.params.stability',
      type: 'select',
      options: [
        { value: 'creative', labelKey: 'canvas.params.stabilityCreative' },
        { value: 'natural', labelKey: 'canvas.params.stabilityNatural' },
        { value: 'robust', labelKey: 'canvas.params.stabilityRobust' },
      ],
      default: 'natural',
    },
  ]
}

function elevenMusicFields(): ModelParamField[] {
  return [
    {
      key: 'duration',
      labelKey: 'canvas.params.musicDuration',
      type: 'select',
      options: [
        { value: '30', labelKey: 'canvas.params.duration30s' },
        { value: '60', labelKey: 'canvas.params.duration1m' },
        { value: '120', labelKey: 'canvas.params.duration2m' },
        { value: '180', labelKey: 'canvas.params.duration3m' },
        { value: '240', labelKey: 'canvas.params.duration4m' },
      ],
      default: '30',
    },
  ]
}

function murekaFields(): ModelParamField[] {
  return [
    {
      key: 'music_mode',
      labelKey: 'canvas.params.musicMode',
      type: 'select',
      options: [
        { value: 'describe', labelKey: 'canvas.params.musicModeDescribe' },
        { value: 'lyrics', labelKey: 'canvas.params.musicModeLyrics' },
      ],
      default: 'describe',
    },
    {
      key: 'instrumental',
      labelKey: 'canvas.params.instrumental',
      type: 'select',
      options: [
        { value: 'false', labelKey: 'canvas.params.no' },
        { value: 'true', labelKey: 'canvas.params.yes' },
      ],
      default: 'false',
    },
  ]
}

function musicFields(musicMode?: string): ModelParamField[] {
  const mode = musicMode === 'lyrics' ? 'lyrics' : 'describe'
  const modeField: ModelParamField = {
    key: 'music_mode',
    labelKey: 'canvas.params.musicMode',
    type: 'select',
    options: [
      { value: 'describe', labelKey: 'canvas.params.musicModeDescribe' },
      { value: 'lyrics', labelKey: 'canvas.params.musicModeLyrics' },
    ],
    default: 'describe',
  }
  const duration: ModelParamField = {
    key: 'duration',
    labelKey: 'canvas.params.duration',
    type: 'number',
    min: 15,
    max: 300,
    default: 60,
  }
  if (mode === 'lyrics') {
    return [
      modeField,
      { key: 'lyrics', labelKey: 'canvas.params.lyrics', type: 'text', default: '' },
      { key: 'style', labelKey: 'canvas.params.musicStyle', type: 'text', default: '' },
      duration,
    ]
  }
  return [
    modeField,
    { key: 'style', labelKey: 'canvas.params.musicStyle', type: 'text', default: '' },
    duration,
  ]
}

function voiceDesignFields(
  model?: AIModel,
  designedVoices?: { value: string; labelKey: string }[],
): ModelParamField[] {
  if (designedVoices?.length) {
    return [
      {
        key: 'voice_id',
        labelKey: 'canvas.params.designedVoiceId',
        type: 'select',
        options: designedVoices,
        default: designedVoices[0]?.value ?? '',
      },
      { key: 'voice_prompt', labelKey: 'canvas.params.voicePrompt', type: 'text', default: '' },
    ]
  }
  const fromSchema = fieldsFromSchema(model, ['voice_id', 'voice_prompt'])
  if (fromSchema.length) return fromSchema
  return [
    { key: 'voice_id', labelKey: 'canvas.params.designedVoiceId', type: 'text', default: '' },
    { key: 'voice_prompt', labelKey: 'canvas.params.voicePrompt', type: 'text', default: '' },
  ]
}

function voiceCloneFields(
  modelId?: string,
  model?: AIModel,
  clonedVoices?: { value: string; labelKey: string }[],
): ModelParamField[] {
  if (isIndexTTSModel(modelId)) {
    const schema = fieldsFromSchema(model, ['voice_id'])
    return schema.length
      ? schema
      : [{ key: 'voice_id', labelKey: 'canvas.params.voice', type: 'text', default: 'jack_cheng' }]
  }
  if (isQwenVCRealtimeModel(modelId)) {
    if (clonedVoices?.length) {
      return [
        {
          key: 'voice_id',
          labelKey: 'canvas.params.voiceCloneId',
          type: 'select',
          options: clonedVoices,
          default: clonedVoices[0]?.value ?? '',
        },
        {
          key: 'language_type',
          labelKey: 'canvas.params.language',
          type: 'select',
          options: QWEN_LANGUAGE_OPTIONS,
          default: 'Auto',
        },
      ]
    }
    return [
      { key: 'voice_id', labelKey: 'canvas.params.voiceCloneId', type: 'text', default: '' },
      {
        key: 'language_type',
        labelKey: 'canvas.params.language',
        type: 'select',
        options: QWEN_LANGUAGE_OPTIONS,
        default: 'Auto',
      },
    ]
  }
  // 克隆无模型/无 schema 时不回退 GEN_AUDIO_PARAMS（避免 mode=bgm）
  return fieldsFromSchema(model)
}

/** 按 Tab + 模型动态参数字段。 */
export function audioParamFieldsForModel(
  modelId?: string,
  model?: AIModel,
  tab?: AudioProductTab,
  clonedVoices?: { value: string; labelKey: string }[],
  designedVoices?: { value: string; labelKey: string }[],
  params?: Record<string, string | number>,
): ModelParamField[] {
  const activeTab = tab ?? inferAudioProductGroup(modelId ?? '')
  if (isElevenLabsMusicModel(modelId)) return elevenMusicFields()
  if (isMurekaMusicModel(modelId)) return murekaFields()
  if (activeTab === 'music' || isSunoMusicModel(modelId)) {
    return musicFields(String(params?.music_mode ?? 'describe'))
  }
  // 配音类：无模型时不展示错位通用参数
  if (!modelId?.trim()) return []
  if (activeTab === 'voice_design' || isQwenVDRealtimeModel(modelId)) return voiceDesignFields(model, designedVoices)
  if (activeTab === 'voice_clone') return voiceCloneFields(modelId, model, clonedVoices)
  if (isElevenLabsTTSModel(modelId)) return elevenTTSFields()
  if (isSeedAudioModel(modelId)) return seedAudioFields()
  if (isMiniMaxSpeechModel(modelId)) return minimaxFields(model)
  if (isQwenDashScopeTTSModel(modelId)) return qwenNarrationFields(modelId, model)
  const schema = fieldsFromSchema(model)
  if (schema.length) return schema
  // 有 modelId 但未识别家族：仅音色+语速，不露 mode 三选一
  return [
    {
      key: 'voice',
      labelKey: 'canvas.params.voice',
      type: 'select',
      options: [
        { value: 'male', labelKey: 'canvas.params.voiceMale' },
        { value: 'female', labelKey: 'canvas.params.voiceFemale' },
        { value: 'narrator', labelKey: 'canvas.params.voiceNarrator' },
        { value: 'child', labelKey: 'canvas.params.voiceChild' },
      ],
      default: 'female',
    },
    { key: 'speed', labelKey: 'canvas.params.speed', type: 'number', min: 0.5, max: 2, default: 1 },
    { key: 'pitch', labelKey: 'canvas.params.pitch', type: 'number', min: -12, max: 12, default: 0 },
  ]
}

export function primaryAudioParamFields(
  modelId?: string,
  model?: AIModel,
  tab?: AudioProductTab,
  clonedVoices?: { value: string; labelKey: string }[],
  designedVoices?: { value: string; labelKey: string }[],
  params?: Record<string, string | number>,
): ModelParamField[] {
  const all = audioParamFieldsForModel(modelId, model, tab, clonedVoices, designedVoices, params)
  if (!all.length) return []
  if (isElevenLabsTTSModel(modelId) || isElevenLabsMusicModel(modelId)) return all
  if (isMurekaMusicModel(modelId)) {
    return all.filter((f) => f.key !== 'instrumental')
  }
  const resolvedTab = tab ?? inferAudioProductGroup(modelId ?? '')
  const fromKey = resolvedTab === 'music' || isSunoMusicModel(modelId) ? 'duration' : 'speed'
  const idx = all.findIndex((f) => f.key === fromKey)
  return idx === -1 ? all.slice(0, 4) : all.slice(0, idx)
}

export function buildDesignedVoiceOptions(
  items: Array<{ voice: string; voice_prompt?: string; language?: string }>,
): { value: string; labelKey: string }[] {
  return items.map((it) => {
    const base = it.voice_prompt?.trim() || shortVoiceLabel(it.voice)
    const label = it.language ? `${base} (${it.language})` : base
    return { value: it.voice, labelKey: label }
  })
}

export function buildClonedVoiceOptions(
  items: Array<{ voice: string; language?: string; created_at?: string }>,
): { value: string; labelKey: string }[] {
  return items.map((it) => ({
    value: it.voice,
    labelKey: it.language ? `${shortVoiceLabel(it.voice)} (${it.language})` : shortVoiceLabel(it.voice),
  }))
}

function shortVoiceLabel(voice: string): string {
  if (voice.length <= 28) return voice
  return `${voice.slice(0, 14)}…${voice.slice(-10)}`
}

export function advancedAudioParamFields(
  modelId?: string,
  model?: AIModel,
  tab?: AudioProductTab,
  clonedVoices?: { value: string; labelKey: string }[],
  designedVoices?: { value: string; labelKey: string }[],
  params?: Record<string, string | number>,
): ModelParamField[] {
  const all = audioParamFieldsForModel(modelId, model, tab, clonedVoices, designedVoices, params)
  if (isElevenLabsTTSModel(modelId) || isElevenLabsMusicModel(modelId)) return []
  if (isMurekaMusicModel(modelId)) {
    return all.filter((f) => f.key === 'instrumental')
  }
  const resolvedTab = tab ?? inferAudioProductGroup(modelId ?? '')
  const fromKey = resolvedTab === 'music' || isSunoMusicModel(modelId) ? 'duration' : 'speed'
  const idx = all.findIndex((f) => f.key === fromKey)
  return idx === -1 ? [] : all.slice(idx)
}
